import { Components } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { CircleCheckFilledIcon } from '@/src/components/icon/filled/CircleCheckFilledIcon';
import { CircleExclamationFilledIcon } from '@/src/components/icon/filled/CircleExclamationFilledIcon';
import { WarningFilledIcon } from '@/src/components/icon/filled/WarningFilledIcon';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';
import { Label } from '@/src/components/label';

export const flowAlert: Components<Theme>['MuiAlert'] = {
  defaultProps: {
    severity: 'info',
    variant: 'filled',
    iconMapping: {
      info: <></>,
      success: <CircleCheckFilledIcon />,
      warning: <CircleExclamationFilledIcon />,
      error: <WarningFilledIcon />,
      notification: <Label type="indicator" variant="neutral" />,
    },
    slotProps: {
      closeIcon: { fontSize: 'extraSmall' },
    },
    slots: {
      closeIcon: CloseIcon,
    },
  },
  styleOverrides: {
    // main wrapper
    root: ({ theme, ownerState }) => ({
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      flexWrap: 'nowrap',
      // specify default text align to prevent overwriting from parent
      textAlign: 'left',
      // style for all variants
      backgroundColor: theme.palette.onSurface.os900,
      color: theme.palette.common.white,
      boxShadow: theme.flowShadows.xs,
      [theme.breakpoints.up('md')]: {
        minWidth: '0',
        boxShadow: theme.flowShadows.m,
      },
      [theme.breakpoints.up('xl')]: {
        boxShadow: theme.flowShadows.xl,
      },
      // proportions
      borderRadius: '4px',
      padding: '8px 16px',
      minWidth: '0',
      width: 'auto',
      maxWidth: '544px',
      // detect if AlertTitle (or previous title) is present in message
      '&:has(.MuiAlert-message .MuiAlertTitle-root), &:has(.MuiAlert-message .MuiTypography-caption1)': {
        '& .MuiAlert-icon': {
          alignSelf: 'flex-start',
        },
      },
      ...(ownerState.severity === 'notification' && {
        backgroundColor: theme.palette.background.primary,
        color: theme.palette.text.primary,
      }),
    }),
    // unsupported variant
    outlined: {
      border: 'none',
    },
    // holder of icon on left side
    icon: ({ theme, ownerState }) => ({
      alignSelf: 'center',
      display: 'block',
      flexShrink: '0',
      position: 'relative',
      opacity: '1',
      // proportions
      padding: '0',
      margin: '0',
      marginRight: '8px',
      // style of each variant
      ...(ownerState.severity === 'info' && { display: 'none' }),
      ...(ownerState.severity === 'notification' && { lineHeight: '14px' }),
      // icons inside
      '& .MuiSvgIcon-root': {
        display: 'block',
        fontSize: '18px',
        ...(ownerState.severity === 'success' && { color: theme.palette.brand.secondary }),
        ...(ownerState.severity === 'warning' && { color: theme.palette.warning.main }),
        ...(ownerState.severity === 'error' && { color: theme.palette.error.main }),
      },
    }),
    // holder of AlertTitle and message in the center
    message: ({ theme }) => ({
      alignSelf: 'center',
      // fill remaining space
      flexGrow: '1',
      // proper typography
      ...theme.typography.disclaimer1,
      // proportions
      padding: '0',
      // multi-line ellipsis
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: '2',
      // detect if AlertTitle (or previous title) is present
      '&:has(.MuiAlertTitle-root), &:has(.MuiTypography-caption1)': {
        WebkitLineClamp: '3',
      },
      [theme.breakpoints.down('md')]: {
        WebkitLineClamp: '3',
        '&:has(.MuiAlertTitle-root), &:has(.MuiTypography-caption1)': {
          WebkitLineClamp: '4',
        },
      },
    }),
    // holder of action button on right side
    action: ({ theme }) => ({
      flexShrink: '0',
      alignItems: 'center',
      // proportions
      padding: '0',
      margin: '0',
      paddingLeft: '8px',
      marginLeft: '16px',
      marginRight: '-4px',
      // divider between message and actions
      borderLeft: `1px solid ${theme.palette.onSurface.os600}`,
    }),
  },
};
